import React from 'react';
import I18n from 'i18n-js';
import {
  BuyItNowPaymentOptionsFieldsFragment,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
} from '@reverbdotcom/commons/src/gql/graphql';
import { BuyItNowStandardOption } from './BuyItNowStandardOption';
import { RCModal, RCAlertBox, RCLoadingBars } from '@reverbdotcom/cadence/components';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';

import { ErrorAlertBox } from '@reverbdotcom/commons/src/ErrorAlertBox';
import { groupedPaymentMethods } from './checkout/paymentMethodGroups';
import experiments from '@reverbdotcom/commons/src/experiments';
import { BuyItNowExpressPayOptions } from './BuyItNowExpressPayOptions';
import { ExpressPaySeparator } from './ExpressPaySeparator';
import { gql } from '@reverbdotcom/commons/src/gql';

type Listing = BuyItNowPaymentOptionsFieldsFragment;

interface Props {
  listing: Listing;
  quantity: number;
  modalOpen: boolean;
  onRequestClose: () => any;
  onStartCheckoutSuccess: (redirectUrl: string, paymentMethodType: CheckoutPaymentMethodType) => any;
}

export function BuyItNowPaymentOptions(props: Props) {
  const [isLoading, setLoading] = React.useState(false);
  const [paymentMethodErrors, setErrors] = React.useState([]);
  const user = useUser();
  const { listing, quantity } = props;
  const standalonePayPalExp = useExpEnabled(experiments.STANDALONE_PAYPAL_BIN);
  const paymentMethods = groupedPaymentMethods(mapPaymentMethods(listing?.acceptedPaymentMethods), standalonePayPalExp);
  const showExpressPay = paymentMethods.express.length > 0;
  const buyerAndSellerInSameCountry = user.countryCode === listing.shop?.address?.countryCode;
  const sellerCountry = listing.shop?.address?.country?.name;
  const expressPaymentMethodTypes = paymentMethods.express.map((paymentMethod) => paymentMethod.type);

  if (!listing) return null;

  function onRequestClose() {
    setErrors([]);
    props.onRequestClose();
  }

  return (
    <RCModal
      isOpen={props.modalOpen}
      onOpenChange={onRequestClose}
      title={I18n.t('discovery.paymentMethodSelection.choosePaymentMethod')}
    >
      {!buyerAndSellerInSameCountry &&
        <div className="mb-4">
          <RCAlertBox type="info">
            {I18n.t('discovery.checkout.buyItNow.notInSameCountry', {
              sellerCountry: sellerCountry,
            })}
          </RCAlertBox>
        </div>
      }
      {paymentMethodErrors.length > 0 &&
        <div className="mb-4">
          <ErrorAlertBox errors={paymentMethodErrors} />
        </div>
      }
      <div className="cart-payment-methods">
        {showExpressPay &&
          <>
            <div className="weight-bold mb-4 align-center">
              {I18n.t('discovery.checkout.expressCheckout.title')}
            </div>
            <BuyItNowExpressPayOptions
              listing={listing}
              paymentMethods={expressPaymentMethodTypes}
              onError={setErrors}
              quantity={quantity}
            />
            <div className="mtb-2">
              <ExpressPaySeparator />
            </div>
          </>
        }
        <ul className="d-flex gap-2 fx-dir-col">
          {paymentMethods.standard.map(paymentMethod =>
            <li key={paymentMethod.type}>
              <BuyItNowStandardOption
                paymentMethod={paymentMethod.type}
                listingId={listing.id}
                onSuccess={props.onStartCheckoutSuccess}
                onError={setErrors}
                setLoading={setLoading}
                disabled={isLoading}
                quantity={quantity}
              />
            </li>,
          )}
        </ul>
      </div>
      {isLoading &&
        <div className="d-flex fx-justify-center mtb-2">
          <RCLoadingBars />
        </div>
      }
    </RCModal>
  );
}

function mapPaymentMethods(acceptedPaymentMethods: string[]) {
  if (!acceptedPaymentMethods) {
    return [];
  }

  return acceptedPaymentMethods.map((paymentMethod) => ({ type: paymentMethod.toUpperCase() as CheckoutPaymentMethodType }));
}

export const BuyItNowPaymentOptionsFragment = gql(`
  fragment BuyItNowPaymentOptionsFields on Listing {
    _id
    acceptedPaymentMethods
    sellerId
    shop {
      _id
      address {
        _id
        countryCode
        country {
          _id
          name
        }
      }
    }
    shipping {
      localPickupOnly
    }
    pricing {
      buyerPrice {
        amount
        amountCents
      }
    }
    ...BuyItNowExpressPayOptionsFields
  }
`);
